import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#333'
  },
  header: {
    marginBottom: 20,
    textAlign: 'center'
  },
  name: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  contact: {
    fontSize: 10,
    color: '#666'
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    marginTop: 15,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    paddingBottom: 2,
    textTransform: 'uppercase'
  },
  block: {
    marginBottom: 8
  },
  expHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2
  },
  bold: {
    fontFamily: 'Helvetica-Bold'
  },
  bullet: {
    marginLeft: 10,
    marginTop: 2,
    flexDirection: 'row'
  },
  bulletContent: {
      flex: 1
  }
});

interface ResumeData {
    name: string;
    contact_info: string;
    summary: string;
    skills: string[];
    experience: Array<{
        title: string;
        company: string;
        duration: string;
        bullets: string[];
    }>;
    education: Array<{
        institution: string;
        degree: string;
        year: string;
    }>;
}

// Safe helper to format skills
const formatSkills = (skills: any): string => {
    if (!skills) return "";
    if (Array.isArray(skills)) return skills.join(" • ");
    if (typeof skills === 'string') return skills;
    return String(skills);
};

export const ResumeDocument = ({ data }: { data: ResumeData }) => (
  <Document>
    <Page size="LETTER" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.name}>{data?.name || "Name"}</Text>
        <Text style={styles.contact}>{data?.contact_info || "Contact Info"}</Text>
      </View>

      {/* Summary */}
      <View style={styles.block}>
        <Text style={styles.sectionTitle}>Summary</Text>
        <Text>{data?.summary || ""}</Text>
      </View>

      {/* Skills */}
      <View style={styles.block}>
        <Text style={styles.sectionTitle}>Skills</Text>
        <Text>{formatSkills(data?.skills)}</Text>
      </View>

      {/* Experience */}
      <View style={styles.block}>
        <Text style={styles.sectionTitle}>Experience</Text>
        {(data?.experience || []).map((exp, i) => (
          <View key={i} style={{ marginBottom: 10 }}>
            <View style={styles.expHeader}>
              <Text style={styles.bold}>{exp.title} | {exp.company}</Text>
              <Text>{exp.duration}</Text>
            </View>
            {(exp.bullets || []).map((b, j) => (
              <View key={j} style={styles.bullet}>
                 <Text>•  </Text>
                 <Text style={styles.bulletContent}>{b}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>

      {/* Education */}
      <View style={styles.block}>
        <Text style={styles.sectionTitle}>Education</Text>
        {(data?.education || []).map((edu, i) => (
          <View key={i} style={styles.expHeader}>
             <Text><Text style={styles.bold}>{edu.institution}</Text> - {edu.degree}</Text>
             <Text>{edu.year}</Text>
          </View>
        ))}
      </View>
    </Page>
  </Document>
);
